import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Badge, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { Tag, Plus, Edit2, CheckCircle, XCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deactivateExpenseCategory,
  activateExpenseCategory,
} from '../services/api';
import { BUILTIN_EXPENSE_CATEGORY_KEYS, formatExpenseCategory } from '../utils/expenseCategories';

const ExpenseCategoriesManager = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getExpenseCategories({ all: true });
      setCategories(res.data);
    } catch {
      toast.error(t('expenseCategoriesManager.errorFetch', 'Error al cargar las categorías'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => { setEditing(null); setName(''); setShowModal(true); };
  const openEdit = (cat) => { setEditing(cat); setName(cat.name); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      if (editing) {
        await updateExpenseCategory(editing.category_id, { name: name.trim() });
        toast.success(t('expenseCategoriesManager.updated', 'Categoría actualizada'));
      } else {
        await createExpenseCategory({ name: name.trim() });
        toast.success(t('expenseCategoriesManager.created', 'Categoría creada'));
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'CATEGORY_EXISTS') {
        toast.error(t('expenseCategoriesManager.exists', 'Ya existe una categoría con ese nombre'));
      } else if (code === 'CATEGORY_RESERVED') {
        toast.error(t('expenseCategoriesManager.reserved', 'Esa categoría ya existe como predeterminada'));
      } else {
        toast.error(err.response?.data?.message || t('expenseCategoriesManager.errorSave', 'Error al guardar'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (cat) => {
    if (cat.is_active) {
      const result = await Swal.fire({
        title: t('expenseCategoriesManager.deactivateTitle', '¿Desactivar categoría?'),
        text: t('expenseCategoriesManager.deactivateConfirm', 'Los gastos ya registrados conservan esta categoría. No aparecerá para gastos nuevos.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'var(--ev-action-danger-bg)',
        cancelButtonColor: 'var(--ev-bg-subtle)',
        confirmButtonText: t('expenseCategoriesManager.deactivate', 'Desactivar'),
        cancelButtonText: t('common.cancel', 'Cancelar'),
      });
      if (!result.isConfirmed) return;
    }
    try {
      if (cat.is_active) {
        await deactivateExpenseCategory(cat.category_id);
        toast.success(t('expenseCategoriesManager.deactivated', 'Categoría desactivada'));
      } else {
        await activateExpenseCategory(cat.category_id);
        toast.success(t('expenseCategoriesManager.activated', 'Categoría activada'));
      }
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || t('expenseCategoriesManager.errorSave', 'Error al guardar'));
    }
  };

  if (loading) {
    return <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
            <Tag size={16} />
            {t('expenseCategoriesManager.title', 'Categorías de Gasto')}
          </h6>
          <small className="text-muted">
            {t('expenseCategoriesManager.subtitle', 'Agrega tus propias categorías además de las predeterminadas')}
          </small>
        </div>
        <Button size="sm" variant="primary" onClick={openCreate}>
          <Plus size={14} className="me-1" />
          {t('expenseCategoriesManager.addButton', 'Agregar Categoría')}
        </Button>
      </div>

      {categories.length === 0 ? (
        <Alert variant="info" className="py-2">
          {t('expenseCategoriesManager.empty', 'Todavía no agregaste categorías propias.')}
        </Alert>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>{t('expenseCategoriesManager.name', 'Nombre')}</th>
              <th>{t('expenseCategoriesManager.status', 'Estado')}</th>
              <th className="text-end">{t('common.actions', 'Acciones')}</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => (
              <tr key={cat.category_id}>
                <td className="fw-medium">{cat.name}</td>
                <td>
                  <Badge bg={cat.is_active ? 'success' : 'secondary'}>
                    {cat.is_active
                      ? t('expenseCategoriesManager.active', 'Activa')
                      : t('expenseCategoriesManager.inactive', 'Inactiva')}
                  </Badge>
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="me-1 py-0 px-1"
                    title={t('common.edit', 'Editar')}
                    onClick={() => openEdit(cat)}
                  >
                    <Edit2 size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant={cat.is_active ? 'outline-warning' : 'outline-success'}
                    className="py-0 px-1"
                    title={cat.is_active
                      ? t('expenseCategoriesManager.deactivate', 'Desactivar')
                      : t('expenseCategoriesManager.activate', 'Activar')}
                    onClick={() => handleToggleActive(cat)}
                  >
                    {cat.is_active ? <XCircle size={13} /> : <CheckCircle size={13} />}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="mt-3">
        <small className="text-muted d-block mb-1">
          {t('expenseCategoriesManager.builtinHeading', 'Categorías predeterminadas (siempre disponibles):')}
        </small>
        <div className="d-flex flex-wrap gap-1">
          {BUILTIN_EXPENSE_CATEGORY_KEYS.map(key => (
            <Badge key={key} bg="light" text="dark" className="border fw-normal">
              {formatExpenseCategory(key, t)}
            </Badge>
          ))}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {editing
              ? t('expenseCategoriesManager.editTitle', 'Editar Categoría')
              : t('expenseCategoriesManager.addTitle', 'Agregar Categoría')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group controlId="expenseCategoryName">
              <Form.Label>{t('expenseCategoriesManager.name', 'Nombre')}</Form.Label>
              <Form.Control
                type="text"
                value={name}
                maxLength={50}
                onChange={e => setName(e.target.value)}
                placeholder={t('expenseCategoriesManager.namePlaceholder', 'Ej: Catering, Fletes')}
                required
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !name.trim()}
            >
              {submitting ? <Spinner as="span" animation="border" size="sm" /> : t('common.save', 'Guardar')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ExpenseCategoriesManager;
